import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { login, user } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    React.useEffect(() => { if (user) nav("/"); }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        const r = await login(email.trim().toLowerCase(), password);
        setBusy(false);
        if (r.ok) nav("/");
        else setErr(r.error || "Log in failed");
    };

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="relative bg-white border border-stone-200 rounded-[10px] overflow-hidden p-8 md:p-10">
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-500" />
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-heading font-bold text-xl">
                            E
                        </div>
                        <div>
                            <h1 className="text-xl font-heading font-semibold text-stone-900 leading-tight">Exprivia Purchase Portal</h1>
                            <div className="text-[11px] uppercase tracking-wider text-stone-500">Vendor quotation management</div>
                        </div>
                    </div>

                    <h2 className="text-2xl font-heading font-semibold text-stone-900 mb-1">Log in</h2>
                    <p className="text-sm text-stone-500 mb-6">Enter your credentials to continue.</p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-medium text-stone-700 mb-1.5 uppercase tracking-wider">Email</label>
                            <input
                                data-testid="login-email"
                                type="email"
                                required
                                autoFocus
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full border border-stone-300 rounded-md bg-white text-sm py-2.5 px-3 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 placeholder:text-stone-400"
                                placeholder="you@exprivia.com"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-xs font-medium text-stone-700 uppercase tracking-wider">Password</label>
                                <Link data-testid="forgot-link" to="/forgot-password" className="text-xs text-amber-700 hover:text-amber-900">Forgot password?</Link>
                            </div>
                            <input
                                data-testid="login-password"
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full border border-stone-300 rounded-md bg-white text-sm py-2.5 px-3 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && (
                            <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                {err}
                            </div>
                        )}
                        <button
                            data-testid="login-submit"
                            type="submit"
                            disabled={busy}
                            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white rounded-md py-2.5 font-medium transition-colors mt-2 flex items-center justify-center gap-2"
                        >
                            {busy && <Loader2 className="animate-spin" size={16} />} Log in
                        </button>
                    </form>
                    <p className="text-xs text-stone-500 text-center mt-5">Accounts are created by your administrator.</p>
                </div>
                <div className="text-center text-[11px] text-stone-400 mt-4 uppercase tracking-wider">Internal use only</div>
            </div>
        </div>
    );
}
